import { Component, OnInit, Inject, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { WordpressService, WpPost, WpCategory, WpQuery } from 'src/app/shared/services/wordpress.service';
import { FormControl } from '@angular/forms';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';

@Component({
  selector: 'app-wordpress-blog-modal',
  templateUrl: './wordpress-blog-modal.component.html',
  styleUrls: ['./wordpress-blog-modal.component.scss']
})
export class WordpressBlogModalComponent implements OnInit, OnDestroy {
  private destroy$: Subject<void> = new Subject<void>();

  posts: WpPost[] = [];
  categories: WpCategory[] = [];
  filteredPosts: WpPost[] = [];
  isLoading = false;
  isLoadingMore = false;
  searchControl = new FormControl('');
  selectedCategoryId: number = 0;
  
  // Pagination
  currentPage = 1;
  postsPerPage = 12;
  totalPosts = 0;
  hasMorePosts = true;
  
  // Infinite scroll
  @ViewChild('scrollContainer') scrollContainer!: ElementRef;
  private scrollThreshold = 300;
  
  constructor(
    public dialogRef: MatDialogRef<WordpressBlogModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private wordpressService: WordpressService
  ) { }

  ngOnInit() {
    this.loadCategories();
    this.loadPosts();
    this.setupSearch();
    this.setupInfiniteScroll();
  }

  ngOnDestroy() {    
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadCategories() {
    const query: WpQuery = {
      page: 1,
      perPage: 100,
      context: 'embed',
      hideEmpty: true
    };
    
    this.wordpressService.getCategories(query).pipe(
      takeUntil(this.destroy$)
    ).subscribe(
      response => {
        this.categories = response.data || [];
      },
      error => {
        console.error('Error loading categories:', error);
      }
    );
  }

  loadPosts(reset: boolean = true) {
    // Prevent duplicate calls
    if (this.isLoading || this.isLoadingMore) {
      return;
    }
    
    if (reset) {
      this.currentPage = 1;
      this.posts = [];
      this.hasMorePosts = true;
      this.isLoading = true;
    } else {
      this.isLoadingMore = true;
    }
    
    const query: WpQuery = {
      page: this.currentPage,
      perPage: this.postsPerPage,
      search: this.searchControl.value || undefined,
      context: "view",
      extraParams: {
        '_embed': 'true',
        'categories': this.selectedCategoryId > 0 ? this.selectedCategoryId.toString() : undefined,
      }
    };
    
    this.wordpressService.getPosts(query).pipe(
      takeUntil(this.destroy$)
    ).subscribe(
      response => {
        const newPosts = response.data || [];
        this.totalPosts = response.page?.totalElements || 0;
        
        if (reset) {
          this.posts = newPosts;
        } else {
          this.posts = [...this.posts, ...newPosts];
        }
        
        // Check if there are more posts to load
        this.hasMorePosts = newPosts.length === this.postsPerPage && this.posts.length < this.totalPosts;
        
        // No need to filter posts anymore since backend handles it
        this.filteredPosts = [...this.posts];
        this.isLoading = false;
        this.isLoadingMore = false;
      },
      error => {
        console.error('Error loading posts:', error);
        this.isLoading = false;
        this.isLoadingMore = false;
      }
    );
  }

  setupSearch() {
    this.searchControl.valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        // Reload posts with search filter
        this.loadPosts(true);
      });
  }

  filterPosts() {
    // Filtering is now handled by backend
    this.filteredPosts = [...this.posts];
  }

  onCategoryChange(categoryId: number | null) {
    this.selectedCategoryId = categoryId;
    // Reload posts with category filter
    this.loadPosts(true);
  }

  getPostImage(post: WpPost): string {
    return post._embedded?.['wp:featuredmedia']?.[0]?.source_url || 'https://altusentertainment.b-cdn.net/static/default.webp';
  }

  getPostCategories(post: WpPost): string {
    if (!post.categories || post.categories.length === 0) {
      return '';
    }
    
    // Map category IDs to category names
    const categoryNames = post.categories.map(catId => {
      const category = this.categories.find(c => c.id === catId);
      return category ? category.name : `Category ${catId}`;
    });
    
    return categoryNames.join(', ');
  }

  getPostAuthor(post: WpPost): string {
    return post._embedded?.['author']?.[0]?.name || 'Unknown';
  }

  getPostDate(post: WpPost): string {
    const dateToUse = post.modified || post.date;
    return dateToUse ? new Date(dateToUse).toLocaleDateString() : '';
  }

  selectPost(post: WpPost) {
    this.dialogRef.close(post);
  }

  close() {
    this.dialogRef.close();
  }

  refreshPosts() {
    this.searchControl.setValue('');
    this.selectedCategoryId = 0;
    this.loadPosts(true);
  }

  clearSearch() {
    this.searchControl.setValue('');
  }

  onEnterSearch() {
    this.loadPosts(true);
  }

  loadMorePosts() {
    if (this.isLoadingMore || !this.hasMorePosts) return;
    
    this.currentPage++;
    this.loadPosts(false);
  }

  setupInfiniteScroll() {
    setTimeout(() => {
      if (this.scrollContainer) {
        const element = this.scrollContainer.nativeElement;
        
        // Always setup scroll listener, regardless of initial scrollability
        element.addEventListener('scroll', () => {
          const scrollPosition = element.scrollTop + element.clientHeight;
          const threshold = element.scrollHeight - this.scrollThreshold;
          
          if (scrollPosition >= threshold && !this.isLoadingMore && this.hasMorePosts) {
            this.loadMorePosts();
          }
        });
      }
    }, 500); // Increased delay to ensure modal is fully rendered
  }
}
